import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  forwardRef,
} from "react";
import { Box, IconButton, Typography } from "@material-ui/core";
import { Check, ChevronLeft, ChevronRight } from "@material-ui/icons";
import { useVerticalNav } from "../../../../../../Contexts/VerticalNavContext";
import { useSelectedTheme, useTranslate } from "../../../../../../Hooks";
import { CheckCircleIcon01 } from "../../../../../../assets/icons";
import {
  CustomButton,
  CustomCard,
  PredefinedPaymentPlanCardListSkeleton,
} from "../../../../../../Components";
import {
  GetPredefinedPaymentPlans,
  CreatePaymentPlan,
} from "../../../../../../Services/PaymentPlanServices";
import {
  createPropertyPaymentPlanDto,
  formatPaymentPlansForTableFields,
} from "../../../../PaymentPlans/models";
import NewPaymentPlansView from "./NewPaymentPlansView";

//  styles
import useStyles from "./styles";

const PaymentPlan = forwardRef(({ propretyId }, ref) => {
  const styles = useStyles();
  const containerRef = useRef();
  const { translate } = useTranslate("NewPropertiesView");
  const { setAlertBoxContent } = useVerticalNav();
  const {
    theme: { palette },
  } = useSelectedTheme();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlans, setSelectedPlans] = useState({});
  const [paymentData, setPaymentData] = useState({
    result: [],
    totalCount: 0,
  });
  const [filter, setFilter] = useState({
    pageIndex: 1,
    pageSize: 10,
    propertyId: propretyId,
  });
  const scrollLeft = () =>
    containerRef.current.scrollBy({ left: -400, behavior: "smooth" });
  const scrollRight = () =>
    containerRef.current.scrollBy({ left: 400, behavior: "smooth" });

  const getPaymentPlans = useCallback(async () => {
    setIsLoading(true);

    try {
      const res = await GetPredefinedPaymentPlans(filter);
      if (!(res && res.status && res.status !== 200)) {
        res.result = formatPaymentPlansForTableFields(res.result);
        setPaymentData({
          result: res?.result,
          totalCount: res?.totalCount,
        });
        const initialSelected = {};
        (res?.result || []).forEach((plan) => {
          initialSelected[plan.propertyPaymentPlanId] =
            plan.isConfigurationSelected;
        });
        setSelectedPlans(initialSelected);
      }
    } catch (error) {
      console.error("something went wrong", error);
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("some-thing-wrong"),
        onClose: () => setAlertBoxContent(null),
      });
    } finally {
      setIsLoading(false);
    }
  });

  const handleCreatePaymentPlan = useCallback(async (selectedPlan) => {
    setIsLoading(true);
    try {
      await CreatePaymentPlan(
        createPropertyPaymentPlanDto({
          ...selectedPlan,
          propertyId: Number(propretyId),
        })
      );

      GetPredefinedPaymentPlans();

      setAlertBoxContent({
        display: true,
        variant: "success",
        title: translate("selected-successfully"),
        onClose: () => setAlertBoxContent(null),
      });
    } catch (error) {
      console.error("something went wrong", error);
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("some-thing-wrong"),
        onClose: () => setAlertBoxContent(null),
      });
    } finally {
      setIsLoading(false);
    }
  });

  const onSelectedPlan = async (plan) => {
    setSelectedPlans((prev) => ({
      ...prev,
      [plan.propertyPaymentPlanId]: true,
    }));
    await handleCreatePaymentPlan(plan);
  };

  useEffect(() => {
    getPaymentPlans();
  }, [propretyId]);

  return (
    <Box>
      {isLoading ? (
        <PredefinedPaymentPlanCardListSkeleton numberOfCards={5} />
      ) : (
        <NewPaymentPlansView
          propertyId={propretyId}
          onDelete={() => getPaymentPlans()}
          ref={ref}
        >
          <Box className={styles.paymentPlanCardsContainer} ref={containerRef}>
            {paymentData.result?.map((paymentPlanItem) => (
              <CustomCard borderRadius="2xl" classes={styles.card}>
                <Box className={styles.infoContainer}>
                  <Typography className={styles.title}>
                    {paymentPlanItem?.paymentPlanName}
                  </Typography>
                  <Typography className={styles.text}>
                    {paymentPlanItem?.description}
                  </Typography>
                </Box>
                <Box className={styles.informativeContainer}>
                  <Box className={styles.detailsContainer}>
                    <Box className={styles.installmentDetail}>
                      <CheckCircleIcon01
                        background={palette.background.success_secondary}
                        foreground={palette.foreground.success}
                      />
                      <Typography className={styles.installmentText}>
                        {paymentPlanItem?.downPayment}{" "}
                        {translate("down-payment")}
                      </Typography>
                    </Box>
                    <Box className={styles.installmentDetail}>
                      <CheckCircleIcon01
                        background={palette.background.success_secondary}
                        foreground={palette.foreground.success}
                      />{" "}
                      <Typography className={styles.installmentText}>
                        {paymentPlanItem?.monthlyInstallment}{" "}
                        {translate("monthly-installment")}
                      </Typography>
                    </Box>
                    <Box className={styles.installmentDetail}>
                      <CheckCircleIcon01
                        background={palette.background.success_secondary}
                        foreground={palette.foreground.success}
                      />{" "}
                      <Typography className={styles.installmentText}>
                        {paymentPlanItem?.handoverPayment}{" "}
                        {translate("handover-payment")}{" "}
                      </Typography>
                    </Box>
                    <Box className={styles.installmentDetail}>
                      <CheckCircleIcon01
                        background={palette.background.success_secondary}
                        foreground={palette.foreground.success}
                      />{" "}
                      <Typography className={styles.installmentText}>
                        {paymentPlanItem?.postHandover}{" "}
                        {translate("post-handover")}
                      </Typography>
                    </Box>
                  </Box>
                  <Box className={styles.actionBtnContainer}>
                    {selectedPlans[paymentPlanItem?.propertyPaymentPlanId] ? (
                      <CustomButton
                        color="primary"
                        startIcon={<Check fontSize="small" />}
                      >
                        {translate("selected")}
                      </CustomButton>
                    ) : (
                      <CustomButton
                        color="secondary"
                        variant="outlined"
                        onClick={() => onSelectedPlan(paymentPlanItem)}
                        disabled={paymentPlanItem?.propertyPlanStatusId !== 1}
                      >
                        {translate("select")}
                      </CustomButton>
                    )}
                  </Box>
                </Box>
              </CustomCard>
            ))}
          </Box>
          <Box className={styles.scrollArrows}>
            <Box>
              <IconButton className={styles.IconButton} onClick={scrollLeft}>
                <ChevronLeft />
              </IconButton>
            </Box>
            <Box>
              <IconButton className={styles.IconButton} onClick={scrollRight}>
                <ChevronRight />
              </IconButton>
            </Box>
          </Box>
        </NewPaymentPlansView>
      )}
    </Box>
  );
});

export default PaymentPlan;
